import React from 'react';
import { supabase } from '../supabase';
import Flag from './Flag';
import {
  isPushSupported, isIosNeedingInstall, permission,
  subscribe, unsubscribe, isSubscribed,
} from '../push';
import { pick, notifyText } from '../i18n';

// How a reader hears from us: the daily email, which languages it arrives in,
// and whether this device also gets a notification.
//
// Shown inside the account menu, and in a shorter form on the login screen.

export const LANGS = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'zh', name: '繁體' },
  { code: 'sc', name: '简体' },
];


const MAX_LANGS = 2;

// Shown wherever push cannot simply be switched on.
export function AppleHelp({ t }) {
  const isMacSafari = typeof navigator !== 'undefined' &&
    /Macintosh/.test(navigator.userAgent) &&
    /Safari/.test(navigator.userAgent) && !/Chrome|Chromium|Edg/.test(navigator.userAgent);

  if (isIosNeedingInstall()) {
    return (
      <div className="np-help">
        <div className="np-help-title">{t.iosTitle}</div>
        <div className="np-help-body">{t.iosSteps}</div>
      </div>
    );
  }
  if (isMacSafari && !isPushSupported()) {
    return (
      <div className="np-help">
        <div className="np-help-title">{t.macTitle}</div>
        <div className="np-help-body">{t.macSteps}</div>
      </div>
    );
  }
  return null;
}

// These settings used to be fetched when the panel opened, so opening it meant
// staring at an empty box for the length of a round trip. They are small and
// rarely change, so the account menu warms them on mount and they are kept
// here — opening the panel then paints immediately.
const cache = new Map();
const inFlight = new Map();
let pushCache = null;

// Reading them takes the account id as well as the address — the pair saved at
// sign-in — since the browser can no longer read the users table directly. A
// session from before accounts had ids gets one moments after load, and the
// warm-up simply runs again then.
export function prefetchPreferences(email, accountId) {
  if (!email || !accountId) return Promise.resolve(null);
  if (cache.has(email)) return Promise.resolve(cache.get(email));
  // Opening the panel while the warm-up is still in the air should join that
  // request rather than start a second one for the same answer.
  if (inFlight.has(email)) return inFlight.get(email);

  const request = supabase
    .rpc('get_preferences', { p_id: accountId, p_email: email })
    .then(({ data, error }) => {
      if (error) { console.error('could not load notification settings', error); return null; }
      const row = data || {};
      const value = {
        on: !!row.daily_email,
        langs: row.email_langs?.length ? row.email_langs : ['en'],
        notifyAt: row.notify_at ? String(row.notify_at).slice(0, 5) : '00:01',
      };
      cache.set(email, value);
      return value;
    })
    .finally(() => inFlight.delete(email));

  inFlight.set(email, request);
  return request;
}

// Saves are optimistic, so the cache has to follow or reopening the panel
// would show the value the reader just changed away from.
function applyToCache(email, patch) {
  const current = cache.get(email);
  if (!current) return;
  if ('daily_email' in patch) current.on = !!patch.daily_email;
  if ('email_langs' in patch) current.langs = patch.email_langs;
  if ('notify_at' in patch) current.notifyAt = String(patch.notify_at).slice(0, 5);
}

export default function NotifyPreferences({ email, accountId, lang = 'en' }) {
  const t = pick(notifyText, lang);
  const warm = email ? cache.get(email) : null;
  const [on, setOn] = React.useState(warm ? warm.on : null);
  const [langs, setLangs] = React.useState(warm ? warm.langs : ['en']);
  const [pushOn, setPushOn] = React.useState(pushCache ?? false);
  const [notifyAt, setNotifyAt] = React.useState(warm ? warm.notifyAt : '00:01');
  const [busy, setBusy] = React.useState(false);
  const [warn, setWarn] = React.useState('');

  // Resolves from the cache on the same tick when the menu has already warmed
  // it, so there is nothing to see loading. The function it falls back to
  // reads the earliest row on the address: some addresses have more than one
  // account row, and erroring out would render nothing at all.
  React.useEffect(() => {
    if (!email || !accountId) return;
    let cancelled = false;
    prefetchPreferences(email, accountId).then(value => {
      if (cancelled) return;
      if (!value) { setOn(false); return; }
      setOn(value.on);
      setLangs(value.langs);
      setNotifyAt(value.notifyAt);
    });
    return () => { cancelled = true; };
  }, [email, accountId]);

  React.useEffect(() => {
    isSubscribed().then(v => { pushCache = v; setPushOn(v); });
  }, []);

  async function save(patch, revert) {
    setBusy(true);
    // Each patch key has a matching p_ argument; the function only accepts these
    // three settings, so nothing else on the account can be changed this way.
    const args = { p_id: accountId, p_email: email };
    Object.entries(patch).forEach(([key, value]) => { args[`p_${key}`] = value; });
    const { error } = await supabase.rpc('update_preferences', args);
    if (error) revert();
    else applyToCache(email, patch);
    setBusy(false);
  }

  function toggleEmail() {
    const next = !on;
    setOn(next);
    save({ daily_email: next }, () => setOn(!next));
  }

  function toggleLang(code) {
    const adding = !langs.includes(code);
    const next = adding ? [...langs, code] : langs.filter(c => c !== code);
    if (next.length === 0) { flash(t.keepOne); return; }
    if (adding && next.length > MAX_LANGS) { flash(t.max); return; }
    const previous = langs;
    setWarn('');
    const ordered = LANGS.map(l => l.code).filter(c => next.includes(c));
    setLangs(ordered);
    save({ email_langs: ordered }, () => setLangs(previous));
  }

  function changeTime(value) {
    const previous = notifyAt;
    setNotifyAt(value);
    save({ notify_at: value }, () => setNotifyAt(previous));
  }

  function flash(msg) { setWarn(msg); setTimeout(() => setWarn(''), 3000); }

  async function togglePush() {
    if (busy) return;
    setBusy(true);
    if (pushOn) { await unsubscribe(); setPushOn(false); }
    else {
      const r = await subscribe(email, accountId);
      setPushOn(r.ok);
      if (!r.ok && r.reason === 'denied') flash(t.blocked);
      else if (!r.ok && r.reason === 'misconfigured') flash('Notifications are not configured on this build.');
      else if (!r.ok && r.reason === 'save-failed') flash('Could not save the subscription. Please try again.');
    }
    setBusy(false);
  }

  if (on === null) return null;

  return (
    <div className="np">
      <label className="np-switch">
        <input type="checkbox" checked={on} onChange={toggleEmail} disabled={busy} />
        <span>{t.email}</span>
      </label>

      <div className={`np-langs${on ? '' : ' is-muted'}`}>
        <div className="np-heading">{t.langs}</div>
        {LANGS.map(l => (
          <label key={l.code} className="np-lang">
            <input
              type="checkbox"
              checked={langs.includes(l.code)}
              onChange={() => toggleLang(l.code)}
              disabled={busy}
            />
            <Flag code={l.code} size={18} />
            <span>{l.name}</span>
          </label>
        ))}
        {langs.includes('es') && <div className="np-note">{t.note}</div>}
      </div>

      <div className="np-when">
        <div className="np-heading">{t.when}</div>
        <select className="np-time" value={notifyAt}
                onChange={e => changeTime(e.target.value)} disabled={busy}>
          {/* Whole hours only: cron ticks once an hour, so a finer choice could
              not be kept. The :01 is that tick. */}
          {Array.from({ length: 24 }, (_, h) => {
            const value = String(h).padStart(2, '0') + ':01';
            const label = new Date(2026, 0, 1, h, 1)
              .toLocaleTimeString(lang === 'es' ? 'es-ES' : lang === 'en' ? 'en-US' : 'zh-TW',
                                  { hour: 'numeric', minute: '2-digit' });
            return <option key={value} value={value}>{label}</option>;
          })}
        </select>
        <span className="np-tz">{t.tz}</span>
      </div>

      <div className="np-push">
        {isPushSupported() && permission() !== 'denied' && (
          <label className="np-switch np-switch-sm">
            <input type="checkbox" checked={pushOn} onChange={togglePush} disabled={busy} />
            <span>{t.push}</span>
          </label>
        )}
        {isPushSupported() && permission() === 'denied' && <div className="np-note">{t.blocked}</div>}
        <div className="np-note">{t.evening}</div>
        <AppleHelp t={t} />
      </div>

      {warn && <div className="np-warn">{warn}</div>}
    </div>
  );
}
